import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Pagination,
  InputGroup,
  Modal,
  Badge,
} from "react-bootstrap";
import {
  FaSearch,
  FaEye,
  FaUser,
  FaEnvelope,
  FaCode,
  FaInfoCircle,
} from "react-icons/fa";
import "./AdminActivity.css";
import { URL } from "../../../config";

const DEFAULT_LIMIT = 10;

// helpers for date/time formatting
const fmtDate = (d) => {
  const dt = new Date(d);
  if (isNaN(dt)) return "-";
  const dd = String(dt.getDate()).padStart(2, "0");
  const m = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][dt.getMonth()];
  const yyyy = dt.getFullYear();
  return `${dd}-${m}-${yyyy}`;
};

const fmtTime = (d) => {
  const dt = new Date(d);
  if (isNaN(dt)) return "-";
  return dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const roleLabel = (role) => {
  const n = Number(role);
  if (n === 1) return "Founder";
  if (n === 2) return "Super Admin";
  if (n === 3) return "Admin";
  return "Admin";
};

const AdminActivity = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    limit: DEFAULT_LIMIT,
    total: 0,
    totalPages: 1,
    search: "",
    adminId: "",
    action: "",
    from: "",
    to: "",
    sortBy: "created_at",
    sortDir: "DESC",
  });
  const [loading, setLoading] = useState(true);

  // filters
  const [search, setSearch] = useState("");
  const [adminId, setAdminId] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const sortBy = "created_at";
  const sortDir = "DESC";

  // modal
  const [selected, setSelected] = useState(null);
  const [showView, setShowView] = useState(false);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        console.warn("No adminToken found for AdminActivity.");
        setRows([]);
        setMeta((m) => ({ ...m, total: 0, totalPages: 1 }));
        return;
      }

      const { data } = await axios.get(`${URL}/admin/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit: DEFAULT_LIMIT,
          search,
          adminId,
          action,
          from,
          to,
          sortBy,
          sortDir,
        },
      });

      if (Array.isArray(data?.data)) {
        setRows(data.data);
      } else {
        setRows([]);
      }

      if (data?.meta) {
        setMeta(data.meta);
      } else {
        setMeta((m) => ({
          ...m,
          page,
          limit: DEFAULT_LIMIT,
          total: Array.isArray(data) ? data.length : 0,
          totalPages: 1,
          search,
          adminId,
          action,
          from,
          to,
          sortBy,
          sortDir,
        }));
      }
    } catch (e) {
      console.error("Failed to load admin activities:", e);
      setRows([]);
      setMeta((m) => ({ ...m, total: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, adminId, action, from, to]);

  const handleOpenView = (row) => {
    setSelected(row);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setSelected(null);
  };

  const getPageItems = (total, current, maxButtons = 7) => {
    const pages = [];
    if (total <= 1) return [1];

    pages.push(1);
    const siblings = Math.floor((maxButtons - 3) / 2);
    let start = Math.max(2, current - siblings);
    let end = Math.min(total - 1, current + siblings);

    if (current <= siblings + 2) end = Math.min(total - 1, maxButtons - 1);
    if (current >= total - (siblings + 1))
      start = Math.max(2, total - (maxButtons - 2));

    if (start > 2) pages.push("start-ellipsis");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push("end-ellipsis");
    if (total > 1) pages.push(total);
    return pages;
  };

  const totalPages = meta.totalPages || 1;

  const badgeForAction = (a) => {
    const key = (a || "").toLowerCase();
    if (key.includes("login"))
      return "adminActivity-badge adminActivity-badge-login";
    if (key.includes("create"))
      return "adminActivity-badge adminActivity-badge-create";
    if (key.includes("update"))
      return "adminActivity-badge adminActivity-badge-update";
    if (key.includes("delete") || key.includes("remove"))
      return "adminActivity-badge adminActivity-badge-delete";
    return "adminActivity-badge adminActivity-badge-generic";
  };

  return (
    <Container fluid className="adminActivity-container">
      <Card className="adminActivity-card">
        <Card.Body>
          <Card.Header className="adminActivity-header">
            <h4 className="adminActivity-title">Admin Activity Logs</h4>
          </Card.Header>

          {/* Filters */}
          <Row className="align-items-center g-2 mb-3">
            <Col lg={4}>
              <InputGroup>
                <InputGroup.Text className="adminActivity-search-ico">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search action / description / admin name / email / endpoint…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="adminActivity-search"
                />
              </InputGroup>
            </Col>
            <Col lg={2}>
              <Form.Control
                type="text"
                placeholder="Admin ID"
                value={adminId}
                onChange={(e) => {
                  setAdminId(e.target.value);
                  setPage(1);
                }}
                className="adminActivity-filter"
              />
            </Col>
            <Col lg={2}>
              <Form.Control
                type="text"
                placeholder="Action code (e.g. ADMIN_CREATE_ORDER)"
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  setPage(1);
                }}
                className="adminActivity-filter"
              />
            </Col>
            <Col lg={2}>
              <Form.Control
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(1);
                }}
                className="adminActivity-filter"
              />
            </Col>
            <Col lg={2}>
              <Form.Control
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(1);
                }}
                className="adminActivity-filter"
              />
            </Col>
          </Row>

          {/* Table */}
          <Table
            striped
            bordered
            hover
            responsive
            className="adminActivity-table"
          >
            <thead>
              <tr>
                <th>#</th>
                <th>Time</th>
                <th>Admin</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Description</th>
                <th>IP</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                rows.map((r, idx) => (
                  <tr key={r.Id}>
                    <td>{(meta.page - 1) * meta.limit + idx + 1}</td>
                    <td>
                      <div className="adminActivity-datetime">
                        <span className="adminActivity-date">
                          {fmtDate(r.created_at)}
                        </span>
                        <span className="adminActivity-time">
                          {fmtTime(r.created_at)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="adminActivity-admin">
                        <div className="adminActivity-admin-name">
                          <FaUser className="me-1" />
                          {r.admin
                            ? r.admin.name || `Admin #${r.adminId}`
                            : `Admin #${r.adminId}`}
                        </div>
                        {r.admin?.email && (
                          <div className="adminActivity-admin-sub">
                            <FaEnvelope className="me-1" />
                            {r.admin.email}
                          </div>
                        )}
                        {r.admin && (
                          <div className="adminActivity-admin-sub">
                            <Badge bg="light" text="dark">
                              {roleLabel(r.admin.role)} (ID: {r.admin.Id})
                            </Badge>
                          </div>
                        )}
                      </div>
                    </td>

                    <td>
                      <span className={badgeForAction(r.action)}>
                        <FaCode className="me-1" />
                        {r.action}
                      </span>
                    </td>
                    <td>
                      <div className="adminActivity-entity">
                        <span className="adminActivity-entity-type">
                          {r.entityType || "-"}
                        </span>
                        {r.entityId && (
                          <span className="adminActivity-entity-id">
                            #{r.entityId}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="adminActivity-endpoint">
                        {/* <span className="adminActivity-method">{r.method || ""}</span>{" "} */}
                        <span className="adminActivity-path">
                          {r.description || "-"}
                        </span>
                      </div>
                    </td>
                    <td>{r.ip || "-"}</td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="adminActivity-view-btn"
                        onClick={() => handleOpenView(r)}
                        title="View details"
                      >
                        <FaEye size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center" }}>
                    No activity found.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center" }}>
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination className="mt-3">
            <Pagination.First
              onClick={() => setPage(1)}
              disabled={meta.page === 1}
            />
            <Pagination.Prev
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={meta.page === 1}
            />
            {getPageItems(totalPages, meta.page).map((item, i) =>
              item === "start-ellipsis" || item === "end-ellipsis" ? (
                <Pagination.Ellipsis key={item + i} disabled />
              ) : (
                <Pagination.Item
                  key={item}
                  active={item === meta.page}
                  onClick={() => setPage(item)}
                >
                  {item}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={meta.page === meta.totalPages}
            />
            <Pagination.Last
              onClick={() => setPage(meta.totalPages)}
              disabled={meta.page === meta.totalPages}
            />
          </Pagination>
        </Card.Body>
      </Card>

      {/* View Modal */}
      <Modal show={showView} onHide={handleCloseView} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            Activity Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <>
              {/* Top summary */}
              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="adminActivity-section-title">Admin</h6>

                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Name</span>
                    <span className="adminActivity-detail-value">
                      {selected.admin
                        ? selected.admin.name || `Admin #${selected.adminId}`
                        : `Admin #${selected.adminId}`}
                    </span>
                  </div>

                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Email</span>
                    <span className="adminActivity-detail-value">
                      {selected.admin?.email || "-"}
                    </span>
                  </div>

                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Role</span>
                    <span className="adminActivity-detail-value">
                      {selected.admin ? roleLabel(selected.admin.role) : "-"}
                    </span>
                  </div>

                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Admin ID</span>
                    <span className="adminActivity-detail-value">
                      {selected.admin?.Id ?? selected.adminId ?? "-"}
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <h6 className="adminActivity-section-title">Activity</h6>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Action</span>
                    <span className="adminActivity-detail-value">
                      {selected.action}
                    </span>
                  </div>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Time</span>
                    <span className="adminActivity-detail-value">
                      {fmtDate(selected.created_at)} •{" "}
                      {fmtTime(selected.created_at)}
                    </span>
                  </div>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">IP</span>
                    <span className="adminActivity-detail-value">
                      {selected.ip || "-"}
                    </span>
                  </div>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">
                      User Agent
                    </span>
                    <span className="adminActivity-detail-value">
                      {selected.userAgent || "-"}
                    </span>
                  </div>
                </Col>
              </Row>

              {/* Entity & endpoint */}
              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="adminActivity-section-title">Entity</h6>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Type</span>
                    <span className="adminActivity-detail-value">
                      {selected.entityType || "-"}
                    </span>
                  </div>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">ID</span>
                    <span className="adminActivity-detail-value">
                      {selected.entityId || "-"}
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <h6 className="adminActivity-section-title">Request</h6>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Method</span>
                    <span className="adminActivity-detail-value">
                      {selected.method || "-"}
                    </span>
                  </div>
                  <div className="adminActivity-detail-row">
                    <span className="adminActivity-detail-label">Endpoint</span>
                    <span className="adminActivity-detail-value">
                      {selected.endpoint || "-"}
                    </span>
                  </div>
                </Col>
              </Row>

              {/* Description */}
              <Row className="mb-3">
                <Col>
                  <h6 className="adminActivity-section-title">Description</h6>
                  <div className="adminActivity-description-box">
                    {selected.description || "-"}
                  </div>
                </Col>
              </Row>

              {/* Meta JSON */}
              <Row>
                <Col>
                  <h6 className="adminActivity-section-title">Meta (Raw)</h6>
                  <div className="adminActivity-meta-box">
                    <pre>
                      {(() => {
                        if (!selected.meta) return "// no meta data";
                        try {
                          const parsed = JSON.parse(selected.meta);
                          return JSON.stringify(parsed, null, 2);
                        } catch {
                          return selected.meta;
                        }
                      })()}
                    </pre>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseView}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminActivity;
